import localforage from "localforage";
import { getShortKey } from "./hashKey";

const CACHE_KEY_META = "imageCacheMeta";
const TTL_DURATION_MS = 1000 * 60 * 60 * 24 * 7; // 7 days
const MAX_CACHE_ENTRIES = 150;

const inMemoryCache = {};
const imageStore = localforage.createInstance({ name: "spaces-image-cache", storeName: "images" });
const metaStore = localforage.createInstance({ name: "spaces-image-cache", storeName: "meta" });


const updateMeta = async (key) => {
  const now = Date.now();
  const metadata = (await metaStore.getItem(CACHE_KEY_META)) || {};
  metadata[key] = { lastUsed: now, addedAt: metadata[key]?.addedAt || now };
  await metaStore.setItem(CACHE_KEY_META, metadata);
};

const enforceLRU = async () => {
  const metadata = (await metaStore.getItem(CACHE_KEY_META)) || {};
  const keys = Object.keys(metadata);

  if (keys.length <= MAX_CACHE_ENTRIES) return;

  // Sort by lastUsed ascending (least used first)
  const sorted = keys.sort((a, b) => metadata[a].lastUsed - metadata[b].lastUsed);
  const toRemove = sorted.slice(0, keys.length - MAX_CACHE_ENTRIES);

  for (let key of toRemove) {
    await imageStore.removeItem(key);
    delete metadata[key];
  }

  await metaStore.setItem(CACHE_KEY_META, metadata);
};

const isExpired = (entry) => {
  if (!entry) return true;
  return Date.now() - entry.addedAt > TTL_DURATION_MS;
};

export const getBase64Image = async (url) => {
  const key = getShortKey(url);
  if (inMemoryCache[key]) return inMemoryCache[key];

  const metadata = (await metaStore.getItem(CACHE_KEY_META)) || {};
  const metaEntry = metadata[key];

  // Check if image exists and not expired
  const cached = await imageStore.getItem(key);
  if (cached && !isExpired(metaEntry)) {
    await updateMeta(key); // Update LRU timestamp
    inMemoryCache[key] = cached;
    return cached;
  }

  // Fetch and convert to base64
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        inMemoryCache[key] = base64;

        try {
          await imageStore.setItem(key, base64);
          await updateMeta(key);
          await enforceLRU();
        } catch (err) {
          console.warn("Failed to save image cache", err);
        }

        resolve(base64);
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error("Image fetch failed:", err);
    return "";
  }
};

export const cleanOldImages = async () => {
  const metadata = (await metaStore.getItem(CACHE_KEY_META)) || {};
  let modified = false;

  for (const [key, value] of Object.entries(metadata)) {
    if (isExpired(value)) {
      await imageStore.removeItem(key);
      delete metadata[key];
      modified = true;
    }
  }

  if (modified) {
    await metaStore.setItem(CACHE_KEY_META, metadata);
  }

  console.log("🧹 Image cache cleanup complete");
};
